<template>
  <section>
    <form
      id="communicatorForm"
      ref="communicatorForm"
      :style="{ backgroundColor: visualSettings.inputBackgroundColor }"
      @submit="submit"
    >
      <div class="v-align">
        <div v-if="attachmentThumb" class="attachment">
          <div class="img" :style="{ backgroundImage }">
            <button type="button" @click="removeAttachment" aria-label="Remove attachment">
              <fa-icon 
                :icon="['fas', 'times-circle']" 
                size="sm" 
              />
            </button>
          </div>
        </div>
        <div class="input">
          <input
            ref="input"
            aria-label="Type message"
            v-model="message"
            :placeholder="placeholder"
            :style="{ backgroundColor: visualSettings.inputBackgroundColor }"
            @keyup="keyUp"
          />
        </div>
        <div class="button" v-if="visualSettings.messageEmojis">
          <div class="wrap">
            <button :class="{ selected: isPickerOpen }" type="button" @click="openEmojiPicker" aria-label="Select emoji">
              <fa-icon 
                :icon="['fas', 'smile']" 
                size="lg" 
              />
            </button>
            <div v-if="isPickerOpen" class="overlay"></div>
          </div>
        </div>
        <div v-if="supportsMessageAttachments" class="button">
          <input 
            ref="communicatorFileUpload"
            aria-label="Upload image"
            type="file"
            @change="fileSelected"
            accept="image/*"
            hidden
          >
          <button type="button" @click="attachFile" aria-label="Attach file">
            <fa-icon 
              :icon="['fas', 'paperclip']" 
              size="lg"
            />
          </button>
        </div>
        <div class="button">
          <button type="submit" aria-label="Send message">
            <fa-icon 
              :icon="['fas', 'paper-plane']" 
              size="lg"
            />
          </button>
        </div>
      </div>

      <div
        v-if="visualSettings.messageEmojis"
        ref="picker"
        class="picker"
        tabindex="0"
        @blur="onPickerBlur"
        @keyup.enter="submit"
      >
        <picker
          class="pickerElement"
          set="apple"
          :style="{ backgroundColor: visualSettings.bodyBackgroundColor }"
          :data="emojiData"
          :show-preview="false"
          :show-search="false"
          :show-categories="false"
          :emoji="'smile'"
          @select="addEmoji"
        />
      </div>
    </form>
    <!-- <div class="typing">{{whoIsTyping}}</div> -->
  </section>
</template>

<script>
import emojiData from 'emoji-mart-vue-fast/data/facebook.json';
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast';
import EmojiNameMap from 'emoji-name-map';
import { mapActions, mapGetters, mapState } from 'vuex';
import 'emoji-mart-vue-fast/css/emoji-mart.css';
import { v4 as uuidv4 } from 'uuid';

const COLONS_REGEX = /:[^:\s]*(?:::[^:\s]*)*:/g;
const CUSTOM_EMOJIS = {
  ':)': ':smile:',
  ':D': ':laughing:',
  ':(': ':disappointed:',
  ';)': ':wink:'
}

export default {
  name: 'Communicator',
  components: {
    Picker
  },
  props: [
    'reply'
  ],
  data() {
    return {
      message: '',
      attachment: '',
      attachmentThumb: '',
      isPickerOpen: false,
      emojiData: new EmojiIndex(emojiData, {
        exclude: ['flags', 'nature', 'foods', 'activity', 'places', 'objects', 'symbols']
      })
    }
  },
  computed: {
    backgroundImage()  {
      return this.attachmentThumb && `url('${this.attachmentThumb}')`;
    },
    placeholder() {
      if(this.reply) {
        return 'Reply...';
      }
      if(this.activeUser.channelId === this.activeChannel.id) {
        return 'Take a note...';
      }
      return `Message ${this.activeChannel.name.toLowerCase()}...`;
    },
    supportsMessageAttachments() {
      return this.visualSettings.messageAttachments && !this.demoMode && !this.reply;
    },
    // whoIsTyping() {
    //   const users = this.activeChannel.typingUsers.filter((user) => {
    //     return user && user.uuid !== this.activeUser.uuid;
    //   }); 
    //   if(users.length === 0) {
    //     return '';
    //   }
    //   let text = users.map(({ name }) => name).slice(0,2).join(users.length > 2 ? ', ' : ' and ');
    //   text += users.length > 2 ? ` and ${users.length - 2} other${users.length - 2 > 1 ? 's' : ''}` : '';
    //   text += ` ${users.length > 1 ? 'are' : 'is'}`;
    //   text += ' typing...';
    //   return text;
    // },
    ...mapGetters([
      'visualSettings'
    ]),
    ...mapState([
      'demoMode',
      'channels',
      'activeUser',
      'activeChannel',
      'activeMessageId',
      'drawerOpen',
      'channelGroup'
    ])
  },
  watch: {
    activeChannel(activeChannel) {
      !this.demoMode && activeChannel && this.$refs.input.focus();
    }
  },
  methods: {
    ...mapActions([
      'sendMessage',
      'createAction'
      // 'stopTyping',
      // 'startTyping'
    ]),
    attachFile(e) {
      e.preventDefault();
      this.supportsMessageAttachments && this.$refs.communicatorFileUpload.click();
    },
    removeAttachment() {
      this.attachment = undefined;
      this.attachmentThumb = undefined;
      if (this.supportsMessageAttachments) {
        this.$refs.communicatorFileUpload.value = null;
      }
    },
    fileSelected() {
      this.attachment = this.$refs.communicatorFileUpload.files[0];
      
      if(this.attachment.size > 5000000) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Attachment Failed',
          text: 'Maximum attachment file size of 5MB exceeded.'
        });
        this.removeAttachment();
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.attachmentThumb = reader.result;
      }, false);
      reader.readAsDataURL(this.attachment);
      this.$refs.input.focus();
    },
    openEmojiPicker() {
      if (!this.isPickerOpen) {
        this.$refs.picker.focus();
        this.isPickerOpen = true;
      }
    },
    onPickerBlur() {
      this.isPickerOpen = false;
    },
    addEmoji(emoji) {
      this.message += emoji.native;
    },
    keyUp() {
      // if (this.message.length === 0) {
      //   return this.stopTyping();
      // }
      
      if (this.visualSettings.messageEmojis) {
        this.message = this.processEmojis(this.message);
      }
      // this.startTyping();
    },
    processEmojis(text) {
      for(let key in CUSTOM_EMOJIS) {
        text = text.replace(key, CUSTOM_EMOJIS[key]);
      }
      return text
        .replace(COLONS_REGEX, (match) => {
          const conversion = EmojiNameMap.get(match);
          return (conversion) ? conversion : match;
        });
    },
    submit(e) {
      e.stopPropagation();
      e.preventDefault();
      if(!this.message.trim().length && !this.attachment) {
        return;
      }

      if(!this.message.trim().length && this.reply) {
        return;
      }

      try {
        if (this.reply) {
          this.createAction({
            messageId: this.activeMessageId,
            channelId: this.activeChannel.id,
            type: 'reply',
            value: `${uuidv4()}:${this.message}`
          });
        } else {
          this.sendMessage({
            message: this.message,
            attachment: this.attachment
          });
        }
        this.message = '';
        this.removeAttachment();
        this.$refs.picker.blur();
      } catch (e) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Unable to send message',
          text: 'Please try again'
        });
      }
    }
  }
}
</script>

<style scoped lang="scss">
section {
  position: relative;
  padding: 0 0 0 20px;
  box-sizing: border-box;
  background: inherit;
  z-index: 1;

  @media only screen and (max-width: 768px) {
    padding-left: 16px;
  }
}

form {
  position: relative;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.1);
  box-sizing: border-box;
  border-radius: 4px;

  .v-align {
    display: table;
    padding: 0 5px 0 12px;
    width: 100%;
    height: 38px;
    box-sizing: border-box;

    @media only screen and (max-width: 768px) {
      padding-left: 10px;
    }
  }
}

.attachment {
  display: table-cell;
  vertical-align: top;
  padding: 5px 12px 0 0;
  width: 1px;

  .img {
    position: relative;
    width: 33px;
    height: 28px;
    border-radius: 4px;
    box-sizing: border-box;
    background-size: auto 100%;
    background-position: center;

    button {
      position: absolute;
      top: -5px;
      right: -5px;
      border-radius: 50%;
      border: 0;
      padding: 2px;
      box-sizing: border-box;
      line-height: 1;
      cursor: pointer;
    }
  }
}

.input {
  display: table-cell;
  vertical-align: top;

  input {
    display: block;
    background: #fff;
    border: 0;
    font-size: 16px;
    font-family: "Poppins";
    -webkit-box-shadow: 0px 6px 12px 0px rgba(0,0,0,0.1);
    box-shadow: var(--input-box-shadow);
    display: block;
    width: 100%;
    border-radius: 4px;
    padding: 12px 12px 12px 0;
    font-size: 16px;
    height: 38px;
    box-sizing: border-box;
    transition: box-shadow .25s ease-out, 
      border-color .2s;

    @media only screen and (max-width: 768px) {
      padding-right: 10px;
    }

    &::placeholder,
    &::-webkit-input-placeholder, 
    :-ms-input-placeholder {
      color: rgb(47,49,56);
    }

    &:focus {
      outline: none;
    }
  }
}

.button {
  display: table-cell;
  vertical-align: middle;
  padding: 4px 0;
  width: 1px;

  .wrap {
    position: relative;

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
      cursor: pointer;
    }
  }

  button {
    width: 30px;
    height: 30px;
    border-radius: 4px;
    border: 0;
    padding: 0;
    background: none;
    color: rgba(47, 49, 56, 0.6);
    cursor: pointer;
    transition: background 250ms ease-out,
      color 250ms ease-out;

    &:focus {
      outline: none;
      box-shadow: none;
      background: none;
      color: rgba(47, 49, 56, 0.6);
    }

    &.selected,
    &:hover {
      background: rgba(234, 235, 239, 0.5);
      color: rgb(47, 49, 56);
    }
  }
}

.typing {
  font-size: 9px;
  font-style: italic;
  line-height: 1;
  margin: 5px 16px;

  @media only screen and (max-width: 768px) {
    margin-left: 12px;
    margin-right: 12px;
  }
}

.picker {
  position: absolute;
  bottom: 46px;
  right: 0;
  max-width: 100%;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(60, 40, 40, 0.12);
  height: 0;
  opacity: 0;
  box-sizing: border-box;
  overflow: hidden;
  transition: opacity 750ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
  
  > * {
    height: 210px;
    border: 0;
    border-radius: 0;
  }

  ::v-deep {
    .emoji-mart {
      font-family: Poppins, Avenir, Helvetica, Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      color: rgb(47, 49, 56);
      max-width: 100%;
    }

    .emoji-mart-emoji,
    .emoji-type-image {
      cursor: pointer;
    }

    .emoji-mart-scroll {
      scrollbar-color: rgba(47, 49, 56, 0.6) rgba(0,0,0,0);
    }

    .emoji-mart-category-label {
      margin-top: 6px;
      
      span {
        font-size: 12.8px;
        color: #2f3138;
        font-weight: bold;
        background-color: inherit;
      }
    }
  }

  &:focus {
    height: auto;
    opacity: 1;
    outline: none;
  }
}
</style>
